import React, { useState, useMemo, useCallback } from 'react';
import { Button } from 'react-admin';
import {
  Grid,
  makeStyles,
  createStyles,
  CircularProgress
} from '@material-ui/core/';
import { useFormState } from 'react-final-form';
import { get, filter, isEmpty, replace, isEqual } from 'lodash';
import moment from 'moment';
import { saveAs } from 'file-saver';

import AdminReportApi from '../../core/apis/AdminReportApi';

import ReportDatagrid from './ReportDatagrid';
import ReportFilters from './ReportFilters';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      height: '100%'
    },
    button: {
      marginTop: '23px'
    },
    labelButton: {
      whiteSpace: 'nowrap'
    },
    datagridContainer: {
      height: '100%',
      overflow: 'hidden',
      paddingRight: '2px'
    },
    gridLg3: {
      // Styles when there is no filters
      '&:only-child': {
        alignSelf: 'flex-end',
        marginBottom: '15px',
        '& .MuiButton-root': {
          marginTop: '0'
        }
      },
      // Styles when the screen is smaller
      [theme.breakpoints.down('md')]: {
        marginBottom: '15px',
        '& .MuiButton-root': {
          marginTop: '0'
        }
      }
    }
  })
);

const ReportSummary = props => {
  const { record } = props;
  const classes = useStyles();
  const formState = useFormState();
  const notifyServerError = useNotifyServerError();
  const [reportData, setReportData] = useState([]);
  const [previousFiltersSent, setPreviousFiltersSent] = useState();
  const [filtersSentAreDifferent, setFiltersSentAreDifferent] = useState(false);
  const [loadingViewReport, setLoadingViewReport] = useState(false);
  const [loadingDownloadReport, setLoadingDownloadReport] = useState(false);

  const filters = record.reportDefinition.filters;

  // Disable buttons when all filters are not filled
  const isEveryButtonDisabled = useMemo(() => {
    const nbFilters = filters.length;
    // If there is no filters to use, enables buttons
    if (nbFilters === 0) {
      return false;
    }

    const filtersRequired = filters.filter(filter => filter.required);
    // If there is no required filters to use, enables buttons
    if (filtersRequired.length === 0) {
      return false;
    }

    const filtersUsed = Object.keys(get(formState, 'values.filters', {}));

    // Get filters required in the filters list used
    const filtersRequiredUsed = filtersRequired.filter(item => {
      return filtersUsed.includes(item.parameter);
    });

    // If all required filters are not filled, disables buttons
    if (filtersRequired.length !== filtersRequiredUsed.length) {
      return true;
    }

    // If all filters are filled, checks values
    const filtersValues = Object.values(get(formState, 'values.filters', {}));
    // If all filters values are filled, checks values content
    if (filtersValues.length > 0) {
      // Get the list of the empty values
      const emptyFiltersRequired = filter(
        get(formState, 'values.filters', {}),
        (item, key) => {
          const currentFilter = filters.find(
            filter => filter.parameter === key
          );
          // Check only for the required filters
          return currentFilter.required && isEmpty(item);
        }
      );
      // If some values are empty, disables buttons
      return emptyFiltersRequired.length > 0;
    } else {
      return true;
    }
  }, [formState, filters]);

  /**
   * Gets and formats the filters to be sent in query parameters
   */
  const getFiltersToSend = useCallback(() => {
    let reportFilters = {};

    Object.keys(get(formState, 'values.filters', {})).forEach(key => {
      if (key.indexOf(',') > -1) {
        // Gets value for the key with a comma and split them into array
        const values = formState.values.filters[key].split('|');
        // Removes white in key to avoid request issues
        // And splits the key to get all parameters to send
        replace(key, ' ', '')
          .split(',')
          .forEach((subKey, index) => {
            // Stores each key and it value as a filter to send
            reportFilters[subKey] = values[index];
          });
      } else {
        if (!isEmpty(formState.values.filters[key])) {
          // Stores the key and it value as a filter to send
          reportFilters[key] = formState.values.filters[key];
        }
      }
    });

    return reportFilters;
  }, [formState]);

  /**
   * Gets and formats the filter labels to be sent in query parameters,
   * Only for the excel export
   */
  const getFilterLabelsToSend = useCallback(() => {
    let reportFilterLabels = {};

    Object.keys(get(formState, 'values.filters', {})).forEach(key => {
      const currentFilter = filters.find(item => item.parameter === key);

      // Check if it's the format for the date with a comma
      if (currentFilter.type === 'DateRange') {
        // Gets value for the key with a comma and split them into array
        let values = formState.values.filters[key].split('|');

        const isAllDates = values.reduce(
          (last, currentDate) => isEmpty(currentDate),
          false
        );

        if (isAllDates) {
          values = 'All dates';
        } else {
          values = values
            .map(date => moment(date).format('MM/DD/YYYY'))
            .join(' - ');
        }

        reportFilterLabels[currentFilter.label] = values;
      } else {
        let values = formState.values.filters[key];
        if (
          currentFilter.type.includes('Select') ||
          currentFilter.type.includes('MultiRadio')
        ) {
          if (Array.isArray(formState.values.filters[key])) {
            if (
              get(formState, `values.allValuesCheckedFilters[${key}]`, false)
            ) {
              values = 'All ' + currentFilter.label;
            } else {
              values = (formState.values.filters[key] || [])
                .map(item => {
                  return currentFilter.lov.find(({ value }) => value === item)
                    .label;
                })
                .join(', ');
            }
          } else {
            const currentFilterValue = currentFilter.lov.find(
              item => item.value === values
            );
            if (currentFilterValue) values = currentFilterValue.label;
          }
        }
        // Store the label and its value label to send
        reportFilterLabels[currentFilter.label] = values;
      }
    });

    return reportFilterLabels;
  }, [formState, filters]);

  /**
   * Click on button 'View Report' to get all rows of the report
   */
  const handleClickViewReport = useCallback(() => {
    setLoadingViewReport(true);

    const reportFilters = getFiltersToSend();

    // If report filters sent are different then force reset pagination
    if (!isEqual(reportFilters, previousFiltersSent)) {
      setPreviousFiltersSent(reportFilters);
      setFiltersSentAreDifferent(true);
    } else {
      setFiltersSentAreDifferent(false);
    }

    AdminReportApi.download(record.id, 'data_json', reportFilters)
      .then(({ data }) => {
        // store report data
        setReportData(data);
        setLoadingViewReport(false);
      })
      .catch(error => {
        setLoadingViewReport(false);
        if (error.message === 'report_has_more_data') {
          setReportData('report_has_more_data');
        } else if (error.message === 'report_has_bad_criteria') {
          setReportData('report_has_bad_criteria');
        } else {
          notifyServerError(error);
        }
      });
  }, [notifyServerError, record, getFiltersToSend, previousFiltersSent]);

  /**
   * Click on button 'Download Report' to download the excel file
   */
  const handleClickDownloadReport = useCallback(() => {
    setLoadingDownloadReport(true);

    const reportFilters = getFiltersToSend();
    const reportFilterLabels = getFilterLabelsToSend();

    AdminReportApi.download(
      record.id,
      'excel',
      reportFilters,
      reportFilterLabels
    )
      .then(resp => {
        setLoadingDownloadReport(false);
        //fileName (built from backend) is located in header, under content-disposition,
        //in filename field. We are using split string in order to get it, based on separators(= and ;)
        //and also, it should start from index 1 and end at -1 because there is '/' in
        var fileName = resp.headers['content-disposition']
          .split('filename=')[1]
          .split(';')[0]
          .slice(1, -1);
        saveAs(resp.data, fileName);
      })
      .catch(error => {
        setLoadingDownloadReport(false);
        if (error.message === 'report_has_more_data') {
          setReportData('report_has_more_data');
        } else if (error.message === 'report_has_bad_criteria') {
          setReportData('report_has_bad_criteria');
        } else {
          notifyServerError(error);
        }
      });
  }, [
    record,
    setLoadingDownloadReport,
    notifyServerError,
    getFiltersToSend,
    getFilterLabelsToSend
  ]);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className={classes.container}
    >
      {/* Header for filters and buttons */}
      <Grid
        container
        justifyContent="space-between"
        wrap="wrap"
        direction={filters.length > 0 ? 'row' : 'column'}
      >
        {/* Component for filters */}
        <ReportFilters filters={filters} />
        {/* Buttons to get rows or Excel file */}
        <Grid
          container
          item
          wrap="nowrap"
          lg={3}
          spacing={2}
          justifyContent="flex-end"
          classes={{ 'grid-lg-3': classes.gridLg3 }}
        >
          <Grid item>
            <Button
              variant="contained"
              label="View Report"
              onClick={handleClickViewReport}
              disabled={isEveryButtonDisabled || loadingViewReport}
              classes={{ button: classes.button, label: classes.labelButton }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              label="Download as Excel"
              onClick={handleClickDownloadReport}
              disabled={isEveryButtonDisabled || loadingDownloadReport}
              classes={{ button: classes.button, label: classes.labelButton }}
              alignIcon="left"
            >
              {loadingDownloadReport ? <CircularProgress size={20} /> : null}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {/* Component to display rows */}
      <Grid
        container
        item
        xs={12}
        classes={{ root: classes.datagridContainer }}
        direction="column"
      >
        <ReportDatagrid
          loading={loadingViewReport}
          data={reportData}
          filtersSentAreDifferent={filtersSentAreDifferent}
        />
      </Grid>
    </Grid>
  );
};

export default ReportSummary;
